'use client';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { useEffect, useState, useMemo } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { parseDeadline } from './deadline';

const calculateTimeLeft = (deadline: Date) => {
  const now = new Date();
  const difference = deadline.getTime() - now.getTime();

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((difference / (1000 * 60)) % 60);
  const seconds = Math.floor((difference / 1000) % 60);

  return { days, hours, minutes, seconds };
};

const getPlural = (
  value: number,
  singular: string,
  pluralFew: string,
  pluralMany: string,
) => {
  if (value === 1) return singular;
  if (value >= 2 && value <= 4) return pluralFew;
  return pluralMany;
};

interface TimeUnitProps {
  value: number;
  label: string;
}

const TimeUnit: React.FC<TimeUnitProps> = ({ value, label }) => {
  return (
    <div className="col-span-1 flex aspect-square flex-col items-center justify-center gap-0 rounded-[6px] bg-primary">
      <AnimatePresence mode="wait" initial={false}>
        <motion.strong
          key={value}
          className="text-xl font-bold tabular-nums text-white sm:text-2xl"
          initial={{ rotateX: 90, opacity: 0 }}
          animate={{ rotateX: 0, opacity: 1 }}
          exit={{ rotateX: -90, opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {value.toString().padStart(2, '0')}
        </motion.strong>
      </AnimatePresence>
      <p className="text-xs text-white sm:text-sm">{label}</p>
    </div>
  );
};

export function Countdown({ generalLayout }: { generalLayout: GeneralLayout['data'] }) {
  const DEADLINE = useMemo(
    () => parseDeadline(generalLayout?.contact?.eori),
    [generalLayout?.contact?.eori],
  );

  // Keep SSR and the first client render identical, with the full clock's height reserved.
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const locale = useLocaleStore(s => s.currentLocale);

  useEffect(() => {
    if (!DEADLINE) return;
    setTimeLeft(calculateTimeLeft(DEADLINE));
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(DEADLINE));
    }, 1000);
    return () => clearInterval(timer);
  }, [DEADLINE]);

  if (!DEADLINE) {
    return null;
  }

  const { days, hours, minutes, seconds } = timeLeft;

  const dayLabel = locale === 'cs'
    ? getPlural(days, 'den', 'dny', 'dní')
    : getPlural(days, 'deň', 'dni', 'dní');
  const hourLabel = locale === 'cs'
    ? getPlural(hours, 'hodina', 'hodiny', 'hodin')
    : getPlural(hours, 'hodina', 'hodiny', 'hodín');
  const minuteLabel = locale === 'cs'
    ? getPlural(minutes, 'minuta', 'minuty', 'minut')
    : getPlural(minutes, 'minúta', 'minúty', 'minút');
  const secondLabel = locale === 'cs'
    ? getPlural(seconds, 'sekunda', 'sekundy', 'sekund')
    : getPlural(seconds, 'sekunda', 'sekundy', 'sekúnd');

  return (
    <div className="flex flex-col items-start justify-start gap-2">
      <p className="w-full text-center text-sm text-white">Akcia vyprší o</p>
      <div className="grid w-[280px] grid-cols-4 gap-2 sm:w-[340px]">
        <TimeUnit value={days} label={dayLabel} />
        <TimeUnit value={hours} label={hourLabel} />
        <TimeUnit value={minutes} label={minuteLabel} />
        <TimeUnit value={seconds} label={secondLabel} />
      </div>
    </div>
  );
}
